import type { Story, Organization, Project } from "@theirstory/common/api_types/backend";
import update from "immutability-helper";

import type { AsyncTaskStatus, AsyncTaskType } from "../inheritFromWeeb-ui-v2/generated/graphql";
import {
  STORIES_LOAD_FAILED,
  STORIES_LOADED,
  STORIES_LOADING,
  STORIES_ONE_UPDATED,
  STORIES_PARTIAL_UPDATE,
  STORIES_SET_PAGE_VIEW,
  STORIES_CLEAR,
  STORY_TASK_UPDATED,
  STORIES_SORT_SETTING,
  STORIES_GALLERY_FILTERS_LOADED,
  STORIES_GALLERY_FILTER_LOADED,
  STORIES_TOGGLE_SELECTED_STORY,
  STORIES_CLEAR_SELECTED_STORIES,
} from "../../actions/types";

import type { StoryTaskUpdated } from "./actions";

export type AsyncTaskId = string;
export type StoryId = string;

export interface StoryTask {
  id: AsyncTaskId;
  type: AsyncTaskType;
  status: AsyncTaskStatus;
}
export interface StoriesState {
  // Someday I wish to have this defined as a type :(
  // Right now this is a raw MongoDB Story doc in all its glory along
  // with extra stuff that we shove in there in 'loadData'
  stories: Story[];
  isLoading: boolean;
  groupId: string;
  selectedStories: Story[];
  paging: {
    size: number;
    current: number;
    count: number;
    totalItems: number;
  };
  sort: {
    column: string;
    order: "asc" | "desc";
  };
  filters: {
    organizations: Organization[];
    selectedOrganization: { label: string; _id: string };
    projects: Project[];
    selectedProject: { label: string; _id: string };
    deedOfGifts: any[];
    selectedDeedOfGift: { label: string; _id: string };
  };
  asyncTasks: Map<StoryId, StoryTask[]>;
  allStoriesArrayTranscripts: any[];
}

export const initialState: StoriesState = {
  isLoading: false,
  stories: [],
  groupId: null,
  paging: {
    size: 15,
    current: 1,
    count: 1,
    totalItems: 0,
  },
  sort: {
    column: null,
    order: null,
  },
  selectedStories: [],
  filters: {
    organizations: [],
    selectedOrganization: { label: "All organizations", _id: "" },
    projects: [],
    selectedProject: { label: "All projects", _id: "" },
    deedOfGifts: [],
    selectedDeedOfGift: { label: "Deed of gift", _id: "" },
  },
  allStoriesArrayTranscripts: [],
  asyncTasks: new Map<StoryId, StoryTask[]>(),
};

const storiesReducer = (state = initialState, { type, payload }: any) => {
  switch (type) {
    case STORIES_CLEAR:
      return initialState;

    case STORIES_LOADING:
      return update(state, {
        isLoading: { $set: true },
        stories: payload?.clear ? { $set: [] } : {},
      });

    case STORIES_LOADED:
      return update(state, {
        isLoading: { $set: false },
        stories: { $set: payload.stories },
        groupId: { $set: payload.groupId },
        paging: {
          current: { $set: payload.page },
          count: { $set: Math.ceil(payload.total / state.paging.size) },
          totalItems: { $set: payload.total },
        },
        allStoriesArrayTranscripts: { $set: payload.allStoriesArrayTranscripts },
      });
    case STORIES_GALLERY_FILTERS_LOADED:
      return update(state, {
        filters: {
          $set: payload.filters,
        },
      });

    case STORIES_GALLERY_FILTER_LOADED:
      return update(state, {
        filters: {
          $set: payload.filters,
        },
        paging: {
          current: { $set: 1 },
        },
      });

    case STORIES_LOAD_FAILED:
      return update(state, {
        isLoading: { $set: false },
      });

    case STORIES_ONE_UPDATED:
      return update(state, {
        stories: (s) => s.map((i) => (i._id !== payload._id ? i : payload)),
      });

    case STORIES_PARTIAL_UPDATE:
      return update(state, {
        stories: (s) => s.map((i) => (i._id !== payload._id ? i : update(i, { $merge: payload }))),
      });

    case STORIES_SET_PAGE_VIEW: {
      const { currentPage, pageSize } = payload;
      return update(
        state,
        pageSize != null
          ? {
              paging: {
                size: { $set: pageSize },
                current: { $set: currentPage != null ? currentPage : 1 },
              },
            }
          : currentPage == null
          ? {}
          : { paging: { current: { $set: currentPage } } }
      );
    }
    case STORY_TASK_UPDATED: {
      const { storyId, taskType, status, id } = payload as StoryTaskUpdated;
      const oldTasks = state.asyncTasks.get(storyId) || [];
      const newTasks: StoryTask[] = [...oldTasks.filter((t) => t.id !== id), { type: taskType, status, id }];
      return update(state, {
        asyncTasks: { $add: [[storyId, newTasks]] },
      });
    }
    case STORIES_SORT_SETTING: {
      const { column, order } = payload;
      return update(state, {
        sort: {
          column: { $set: column },
          order: { $set: order },
        },
      });
    }
    case STORIES_TOGGLE_SELECTED_STORY: {
      const story = payload;
      const isSelected = state.selectedStories.some((s) => s._id === story._id);
      return update(state, {
        selectedStories: isSelected
          ? { $set: state.selectedStories.filter((s) => s._id !== story._id) }
          : { $push: [story] },
      });
    }
    case STORIES_CLEAR_SELECTED_STORIES: {
      return update(state, {
        selectedStories: { $set: [] },
      });
    }
    default:
      return state;
  }
};

export default storiesReducer;
